/**
 * The world generator.
 *
 * Everything the universe contains is derived here from the world seed. Nothing
 * is stored and no function reads anything but its arguments — so any server, at
 * any time, reconstructs an identical universe.
 *
 * One thing is memoised: the cluster layout. Clusters have to be positioned as a
 * set rather than one at a time, because "overlaps nothing else" is not a
 * property a cluster has on its own. It stays a pure function of the seed — same
 * seed, same galaxy — and the cache only avoids recomputing it per lookup.
 *
 * ── Stability ───────────────────────────────────────────────────────────────
 *
 * Editing this file changes the universe. That is not a bug to be fixed, it is
 * the nature of a seed-driven world: it is a function of (seed, algorithm), and
 * changing either moves planets under players' feet. Two mitigations:
 *
 *   * `GENERATOR_VERSION` is recorded in `world_meta`, so a service can refuse
 *     to boot against a database built by a different generator rather than
 *     quietly corrupting it. Bump it whenever output changes.
 *   * every entity draws from its own stream, keyed by its identity — so adding
 *     a field to planet generation cannot shift the stations. Changes stay
 *     local to what was actually edited.
 *
 * The version is deliberately *not* mixed into the seeds. It exists to detect
 * divergence, not to cause it — folding it in would guarantee that every bump
 * churns the entire universe even when the change was cosmetic.
 */
import { makeDesignatedName, makeName, makeStationName } from './names.js';
import { rollMoonPalette, rollPlanetPalette, rollStarColor } from './color.js';
import { Rng } from './rng.js';
import { GAS_GIANT_MIN_RADIUS, SIZE_CLASSES, MOON_MIN_PLANET_RADIUS, SUN_ORBIT_SCORE, apoapsis, periapsis, stationSizeScore, } from './types.js';
/** Bump whenever anything in this file changes what gets generated. */
export const GENERATOR_VERSION = 14;
/**
 * Universe scale.
 *
 * Nothing technical constrains these. Generation is lazy and stores nothing, so
 * a system that no one has visited costs exactly zero — the real limit is
 * player density: spread a small population over too many systems and nobody
 * ever meets anyone. Tune here, then run `node scripts/analyse-world.mjs` to
 * see what it did.
 */
export const CLUSTER_COUNT = 700;
const SYSTEMS_PER_CLUSTER = { min: 10, max: 40 };
/**
 * Galactic layout.
 *
 * Clusters sit on a phyllotaxis (golden-angle) spiral: the arrangement
 * sunflower seeds use, which spaces points evenly with no two ever landing on
 * top of each other. That matters because clusters must read as distinct blobs
 * on a single galaxy-wide map — random placement would let them overlap and
 * smear into one another.
 *
 * A cluster's own radius scales with the square root of its system count, so a
 * 40-system cluster is wider than a 10-system one but no denser.
 */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
/**
 * Spacing, radius and offset are one interlocked set, and they cannot all be
 * maximised: bigger clusters, tighter packing and stronger disruption of the
 * spiral all compete for the same room.
 *
 * The invariant was once "bounding spheres never intersect", which forced the
 * spacing far wider than the clusters looked like they needed. It is now
 * "cores never intersect": neighbouring clusters may interpenetrate at the
 * edges, where stars are sparse anyway, but their dense centres stay clear. See
 * `MIN_CLUSTER_SEPARATION_RATIO` — the test measures against that.
 */
const CLUSTER_SPACING = 6800;
const CLUSTER_CORE_RADIUS = 650;
/**
 * How many times the height-separation pass sweeps before giving up.
 *
 * Moving one cluster can push it into a third, so this iterates rather than
 * sweeping once. It converges quickly — the corrections shrink every pass — and
 * the cap is a guard against a pathological seed rather than an expected limit.
 */
const SEPARATION_PASSES = 120;
/**
 * How much of each correction to apply per pass.
 *
 * Under-relaxing: applying the full correction makes pairs overshoot into each
 * other's old space and oscillate instead of settling.
 */
const SEPARATION_RELAXATION = 0.6;
/**
 * Separate to slightly more than the floor.
 *
 * Relaxation approaches its target from below and stops when corrections get
 * small, which lands pairs on the limit rather than past it — and a pair sitting
 * at exactly the minimum is one rounding error from violating it. Aiming a
 * little beyond means the guarantee holds with room to spare.
 */
const SEPARATION_MARGIN = 1.04;
/**
 * Per-cluster looseness, on top of the square-root scaling with system count.
 *
 * This is what widens the size range rather than simply making everything
 * bigger: a dense cluster packs its stars tight while a sparse one of the same
 * population sprawls, so the galaxy has genuinely different-looking regions.
 */
const CLUSTER_SPREAD = { min: 0.6, max: 2.0 };
/**
 * Pure phyllotaxis is *too* even. At 500 clusters the golden-angle spiral is
 * plainly visible from above as Fibonacci arms, which reads as manufactured.
 *
 * Breaking it needs displacement in both directions — radial noise blurs the
 * concentric rings, tangential noise blurs the arms — and both are expressed as
 * fractions of the spacing rather than absolute distances, so the disruption
 * stays proportional at every radius instead of vanishing near the rim.
 *
 * The *minimum* matters as much as the maximum: with an offset drawn from zero
 * upward, a good fraction of clusters land near their ideal spiral point and
 * the arms stay legible through the noise. Forcing every cluster at least this
 * far off its mark is what actually destroys the pattern.
 */
const CLUSTER_OFFSET = { min: 0.1, max: 0.35 };
/**
 * How close two cluster centres may come, as a fraction of their combined
 * radii. Below 1 the bounding spheres overlap; this is the floor on how much.
 */
export const MIN_CLUSTER_SEPARATION_RATIO = 0.75;
/** A displacement of at least the minimum, in either direction. */
function clusterOffset(rng) {
    const magnitude = rng.range(CLUSTER_OFFSET.min, CLUSTER_OFFSET.max) * CLUSTER_SPACING;
    return rng.bool() ? magnitude : -magnitude;
}
/**
 * The galaxy is a squashed ball, not a plate.
 *
 * Height runs to about a third of the span, with the middle thicker than the
 * rim the way a real spiral galaxy bulges at its core. That is far deeper than
 * it started: at a twenty-sixth of the span there was simply not enough height
 * for the eye to read the third dimension at all, and the whole thing looked
 * painted on. `GALACTIC_BULGE_FALLOFF` is how quickly the thickness decays
 * outward, in units of cluster spacing.
 *
 * This is the *starting* scatter. Height separation adds to it wherever the
 * spiral packs clusters too close, so the galaxy ends up thicker still, and
 * thickest exactly where it is most crowded.
 */
const GALACTIC_THICKNESS = 62_000;
const GALACTIC_BULGE_FALLOFF = 9;
/**
 * The warp — the thing that actually makes the galaxy read as three-dimensional.
 *
 * Height used to be nothing but random scatter about z = 0. Scatter has no
 * *shape*: from outside it is a flat plate with fuzz on it, and from inside the
 * fuzz is too small to register against the span, so the disc looked flat from
 * every angle however much thickness was added. Making the scatter bigger only
 * makes a thicker plate.
 *
 * Depth needs structure the eye can follow, so the midplane itself bends. The
 * outer disc lifts on one side and drops on the other — the integral-sign warp
 * real spiral galaxies have, the Milky Way included. It is coherent over huge
 * distances, so from any viewpoint the disc has a visible S-curve running
 * through it, and a cluster's height becomes a fact about where it sits rather
 * than a coin flip.
 *
 * Amplitude is a fraction of the galaxy's own radius, so it stays proportional
 * if the cluster count changes.
 */
const GALACTIC_WARP = 0.13;
/** Fraction of the rim radius inside which the disc stays flat. */
const GALACTIC_WARP_START = 0.3;
/** Which way the warp runs. Arbitrary, but fixed, so the galaxy has an axis. */
const GALACTIC_WARP_PHASE = 0.7;
/** Roughly where the outermost clusters land, from the spiral's own formula. */
const GALAXY_RADIUS = CLUSTER_SPACING * Math.sqrt(CLUSTER_COUNT);
/**
 * Height of the disc's midplane at a point, before any per-cluster scatter.
 *
 * Zero across the flat inner disc, then growing outward and reversing sign
 * across the warp axis.
 */
function midplaneHeight(distance, angle) {
    const reach = distance / GALAXY_RADIUS;
    if (reach <= GALACTIC_WARP_START)
        return 0;
    // Smoothstep rather than a linear ramp, so the bend eases in instead of
    // creasing at the radius where it starts.
    const t = Math.min(1, (reach - GALACTIC_WARP_START) / (1 - GALACTIC_WARP_START));
    const eased = t * t * (3 - 2 * t);
    return GALACTIC_WARP * GALAXY_RADIUS * eased * Math.sin(angle - GALACTIC_WARP_PHASE);
}
/** Clusters are squashed the same way, so they read as lens shapes not spheres. */
const CLUSTER_FLATTEN = 0.45;
function clusterRadius(systemCount, spread) {
    return CLUSTER_CORE_RADIUS * Math.sqrt(systemCount) * spread;
}
/**
 * Where new players start. Generation guarantees this system has a landable
 * outpost and an asteroid field, so the first two tutorial missions are always
 * completable.
 */
export const STARTING_SYSTEM_ID = 'c000-s000';
// ─── Identity ────────────────────────────────────────────────────────────────
const pad = (value, width = 3) => String(value).padStart(width, '0');
export function makeClusterId(index) {
    return `c${pad(index)}`;
}
export function makeSystemId(clusterId, index) {
    return `${clusterId}-s${pad(index)}`;
}
const SYSTEM_ID_PATTERN = /^(c\d{3})-s(\d{3})$/;
export function parseSystemId(systemId) {
    const match = SYSTEM_ID_PATTERN.exec(systemId);
    if (!match?.[1] || !match[2])
        throw new Error(`Malformed system id: ${systemId}`);
    return { clusterId: match[1], index: Number(match[2]) };
}
// ─── Universe and clusters ───────────────────────────────────────────────────
export function getUniverse(seed) {
    return {
        seed,
        generatorVersion: GENERATOR_VERSION,
        clusterIds: Array.from({ length: CLUSTER_COUNT }, (_, i) => makeClusterId(i)),
    };
}
/**
 * Where every cluster sits, resolved together.
 *
 * The one thing in this file computed as a set rather than per entity, because
 * "does not overlap anything else" is not a property a cluster has on its own.
 * The spiral alone used to guarantee separation, which is why nothing needed to
 * look at its neighbours — but that only worked while the galaxy was sparse
 * enough for the spacing to do all the work, and a sparse galaxy is a thin one.
 *
 * Still a pure function of the seed, so every determinism guarantee holds; it is
 * simply memoised, since five hundred placements is a few milliseconds and
 * `getCluster` is called constantly.
 */
const layouts = new Map();
function placeClusters(seed) {
    const placements = [];
    for (let index = 0; index < CLUSTER_COUNT; index++) {
        const rng = Rng.forEntity(seed, 'cluster', makeClusterId(index));
        const systemCount = rng.int(SYSTEMS_PER_CLUSTER.min, SYSTEMS_PER_CLUSTER.max);
        // Golden-angle spiral for the base placement — deliberately not random,
        // because an even spread is the whole point — then pushed off it in both
        // directions, because perfect evenness reads as manufactured.
        const baseAngle = index * GOLDEN_ANGLE;
        const baseDistance = CLUSTER_SPACING * Math.sqrt(index + 0.5);
        const radialOffset = clusterOffset(rng);
        const tangentialOffset = clusterOffset(rng);
        const distance = Math.max(CLUSTER_SPACING * 0.5, baseDistance + radialOffset);
        // Converted to an angle so the same arc length shifts a rim cluster as much
        // as a core one, rather than fading out with radius.
        const angle = baseAngle + tangentialOffset / distance;
        const spread = rng.range(CLUSTER_SPREAD.min, CLUSTER_SPREAD.max);
        // Height is the warped midplane plus scatter about it, thicker toward the
        // core.
        const bulge = Math.exp(-distance / (CLUSTER_SPACING * GALACTIC_BULGE_FALLOFF));
        const z = midplaneHeight(distance, angle) +
            rng.range(-1, 1) * GALACTIC_THICKNESS * (0.35 + 0.65 * bulge);
        placements.push({
            position: { x: Math.cos(angle) * distance, y: Math.sin(angle) * distance, z },
            radius: clusterRadius(systemCount, spread),
            systemCount,
        });
    }
    separateInHeight(placements);
    return placements;
}
/**
 * Push overlapping clusters apart, in height only.
 *
 * Density and thickness are the same problem seen twice. Packing the spiral
 * tighter is what makes the galaxy feel populated, but it is also what makes
 * neighbours collide; height is the free dimension that can absorb that, and
 * using it means the galaxy gets thicker precisely where it got denser.
 *
 * Only z moves. The spiral is what gives the galaxy its shape from above and
 * nudging x or y would blur the arms back out — the very thing the offsets are
 * carefully calibrated to do by exactly the right amount.
 *
 * Two clusters clear each other as soon as they are far enough apart in *any*
 * direction, so a pair already separated across the disc needs no height at
 * all; this only pays for the separation the spiral did not already provide.
 */
function separateInHeight(placements) {
    for (let pass = 0; pass < SEPARATION_PASSES; pass++) {
        let worst = 0;
        for (let i = 0; i < placements.length; i++) {
            for (let j = i + 1; j < placements.length; j++) {
                const a = placements[i];
                const b = placements[j];
                const required = (a.radius + b.radius) * MIN_CLUSTER_SEPARATION_RATIO * SEPARATION_MARGIN;
                const dx = a.position.x - b.position.x;
                const dy = a.position.y - b.position.y;
                // Far enough apart across the disc; height is irrelevant to this pair.
                const flat = Math.hypot(dx, dy);
                if (flat >= required)
                    continue;
                // The height difference this pair needs, given how close they already
                // are in the plane.
                const needed = Math.sqrt(required * required - flat * flat);
                const dz = a.position.z - b.position.z;
                const shortfall = needed - Math.abs(dz);
                if (shortfall <= 0)
                    continue;
                worst = Math.max(worst, shortfall);
                // Split the correction between them, and keep whatever order they were
                // already in so the warp's structure survives — a pair straddling the
                // midplane stays straddling it rather than being flipped.
                const direction = dz === 0 ? (i % 2 === 0 ? 1 : -1) : Math.sign(dz);
                const step = (shortfall / 2) * SEPARATION_RELAXATION;
                a.position.z += direction * step;
                b.position.z -= direction * step;
            }
        }
        if (worst < 1)
            return;
    }
}
function galaxyLayout(seed) {
    let layout = layouts.get(seed);
    if (!layout) {
        layout = placeClusters(seed);
        layouts.set(seed, layout);
    }
    return layout;
}
export function getCluster(seed, clusterId) {
    const index = Number(clusterId.slice(1));
    if (!Number.isInteger(index) || index < 0 || index >= CLUSTER_COUNT) {
        throw new Error(`No such cluster: ${clusterId}`);
    }
    const placement = galaxyLayout(seed)[index];
    const nameRng = Rng.forEntity(seed, 'cluster-name', clusterId);
    return {
        id: clusterId,
        name: makeName(nameRng),
        position: placement.position,
        radius: placement.radius,
        systemIds: Array.from({ length: placement.systemCount }, (_, i) => makeSystemId(clusterId, i)),
    };
}
function rollSystemShape(seed, systemId, cluster) {
    const rng = Rng.forEntity(seed, 'system', systemId);
    const nameRng = Rng.forEntity(seed, 'system-name', systemId);
    const isStart = systemId === STARTING_SYSTEM_ID;
    const planetCount = rng.weighted([
        [0, 6], [1, 12], [2, 20], [3, 22], [4, 18], [5, 12], [6, 6],
    ]);
    const rolledStations = rng.weighted([[0, 30], [1, 40], [2, 20], [3, 10]]);
    const rolledFields = rng.weighted([[0, 25], [1, 30], [2, 25], [3, 14], [4, 6]]);
    const anomalyCount = rng.weighted([[0, 55], [1, 32], [2, 13]]);
    // Uniform through the cluster's volume — cbrt on the radius, or systems bunch
    // at the centre and clusters look like bullseyes rather than star fields.
    // Flattened on z so a cluster reads as a lens, matching the galactic disc.
    const distance = cluster.radius * Math.cbrt(rng.float());
    const azimuth = rng.float() * Math.PI * 2;
    const inclination = Math.acos(2 * rng.float() - 1);
    const planar = Math.sin(inclination) * distance;
    return {
        name: makeName(nameRng),
        localPosition: {
            x: Math.cos(azimuth) * planar,
            y: Math.sin(azimuth) * planar,
            z: Math.cos(inclination) * distance * CLUSTER_FLATTEN,
        },
        ...rollStarColor(rng),
        starRadius: rng.range(85, 260),
        planetCount,
        // The starting system is the one place with guaranteed content — the
        // opening tutorials send the player to an outpost and then to a field.
        stationCount: isStart ? Math.max(1, rolledStations) : rolledStations,
        asteroidFieldCount: isStart ? Math.max(1, rolledFields) : rolledFields,
        anomalyCount,
    };
}
/** Validate and return the owning cluster, rejecting systems that do not exist. */
function requireCluster(seed, systemId) {
    const { clusterId, index } = parseSystemId(systemId);
    // Reject systems the cluster does not actually contain, so a typo surfaces
    // rather than silently generating a place that no navigation map lists.
    const cluster = getCluster(seed, clusterId);
    if (index >= cluster.systemIds.length) {
        throw new Error(`No such system: ${systemId} (cluster has ${cluster.systemIds.length})`);
    }
    return { cluster, index };
}
function galacticPositionOf(cluster, shape) {
    return {
        x: cluster.position.x + shape.localPosition.x,
        y: cluster.position.y + shape.localPosition.y,
        z: cluster.position.z + shape.localPosition.z,
    };
}
/**
 * Everything about a system except its contents.
 *
 * This is what the galaxy map reads. It performs the system-level rolls but
 * builds no planets, stations, fields or anomalies, so drawing every system in
 * the universe stays cheap.
 */
export function getSystemSummary(seed, systemId) {
    const { cluster } = requireCluster(seed, systemId);
    const shape = rollSystemShape(seed, systemId, cluster);
    return {
        id: systemId,
        clusterId: cluster.id,
        name: shape.name,
        position: shape.localPosition,
        galacticPosition: galacticPositionOf(cluster, shape),
        starColor: shape.starColor,
        starClass: shape.starClass,
        starRadius: shape.starRadius,
        planetCount: shape.planetCount,
        stationCount: shape.stationCount,
        asteroidFieldCount: shape.asteroidFieldCount,
        anomalyCount: shape.anomalyCount,
    };
}
/** Summaries for a whole cluster, deriving the cluster once rather than per system. */
export function getClusterSummaries(seed, clusterId) {
    const cluster = getCluster(seed, clusterId);
    return cluster.systemIds.map((systemId) => {
        const shape = rollSystemShape(seed, systemId, cluster);
        return {
            id: systemId,
            clusterId,
            name: shape.name,
            position: shape.localPosition,
            galacticPosition: galacticPositionOf(cluster, shape),
            starColor: shape.starColor,
            starClass: shape.starClass,
            starRadius: shape.starRadius,
            planetCount: shape.planetCount,
            stationCount: shape.stationCount,
            asteroidFieldCount: shape.asteroidFieldCount,
            anomalyCount: shape.anomalyCount,
        };
    });
}
/** The whole galaxy, cheaply — every cluster with every system's summary. */
export function getGalaxyMap(seed) {
    return getUniverse(seed).clusterIds.map((clusterId) => ({
        cluster: getCluster(seed, clusterId),
        systems: getClusterSummaries(seed, clusterId),
    }));
}
export function getSystem(seed, systemId) {
    const { cluster } = requireCluster(seed, systemId);
    const shape = rollSystemShape(seed, systemId, cluster);
    const isStart = systemId === STARTING_SYSTEM_ID;
    return {
        id: systemId,
        clusterId: cluster.id,
        name: shape.name,
        position: shape.localPosition,
        galacticPosition: galacticPositionOf(cluster, shape),
        starColor: shape.starColor,
        starClass: shape.starClass,
        starRadius: shape.starRadius,
        ...buildContents(seed, systemId, shape, isStart),
    };
}
/**
 * Planets first, then everything that might orbit one.
 *
 * Stations, fields and anomalies all need to know what planets exist before
 * they can choose a host, and they need the outermost orbit to scale their own
 * distances against — a system with one close-in planet should not scatter its
 * stations across the same volume as one with six.
 */
function buildContents(seed, systemId, shape, isStart) {
    const axes = orbitAxes(seed, systemId, shape.planetCount);
    const planets = axes.map((axis, i) => generatePlanet(seed, systemId, shape.name, i, axis));
    // Falls back to a nominal radius for systems with no planets at all, so
    // stations and anomalies still have a sensible scale to sit at.
    const outerOrbit = axes.length > 0 ? axes[axes.length - 1] : 1800;
    return {
        planets,
        stations: (() => {
            // Shared and mutated across the run, so each planet can host at most one.
            const freePlanets = [...planets];
            return Array.from({ length: shape.stationCount }, (_, i) => 
            // Forcing the first starting-system station to be an outpost keeps the
            // tutorial's "fly to a nearby outpost" honest.
            generateStation(seed, systemId, i, isStart && i === 0 ? 'outpost' : null, freePlanets, outerOrbit));
        })(),
        asteroidFields: generateAsteroidFields(seed, systemId, shape.asteroidFieldCount, planets, outerOrbit),
        anomalies: Array.from({ length: shape.anomalyCount }, (_, i) => generateAnomaly(seed, systemId, i, outerOrbit)),
    };
}
// ─── Contents ────────────────────────────────────────────────────────────────
/**
 * Orbit spacing, as a set of constants that only work together.
 *
 * Orbits grow multiplicatively rather than by a fixed step — real systems space
 * out that way, and it is also what keeps eccentric orbits from crossing.
 *
 * The guarantee: an inner orbit's apoapsis plus both planet radii must stay
 * below the next orbit's periapsis. Because every point on an orbit lies within
 * that orbit's radial shell, disjoint shells mean planets cannot collide *no
 * matter how the orbital planes are tilted* — the separation argument is
 * radial, so 3D inclination costs nothing.
 *
 * Worst case with these numbers: a = 620, growth 1.6, e = 0.06 on both, radii
 * 120 each gives 620*1.6*0.94 - 620*1.06 = 275 against 240 of planet. Change
 * any one of them and re-run the exhaustive orbit test.
 */
const ORBIT_BASE = { min: 620, max: 780 };
const ORBIT_GROWTH = { min: 1.6, max: 1.9 };
const ORBIT_ECCENTRICITY_MAX = 0.06;
const PLANET_RADIUS = { min: 28, max: 120 };
/**
 * Orbital inclination is deliberately unbounded — any tilt up to fully polar is
 * reachable — but heavily biased toward the shallow end.
 *
 * Raising a signed uniform draw to an odd power does that in one step: most
 * values land near zero, the extremes stay reachable, and no clamp is needed.
 * A system of wildly tilted orbits is legal and rare, which is what makes it
 * interesting when it happens rather than noise everywhere.
 *
 * The exponent was cubed first, which left 15% of orbits past 57 degrees —
 * common enough to read as random rather than remarkable. Fifth power puts
 * roughly 70% inside 14 degrees while keeping near-polar orbits possible.
 */
const ORBIT_INCLINATION_RANGE = Math.PI / 2;
const INCLINATION_BIAS = 5;
function rollInclination(rng) {
    const t = rng.range(-1, 1);
    return Math.sign(t) * Math.abs(t) ** INCLINATION_BIAS * ORBIT_INCLINATION_RANGE;
}
/** A circular-ish orbit at a given radius, tilted and oriented at random. */
function makeOrbit(rng, semiMajorAxis, eccentricityMax = ORBIT_ECCENTRICITY_MAX) {
    return {
        semiMajorAxis,
        eccentricity: rng.range(0, eccentricityMax),
        inclination: rollInclination(rng),
        ascendingNode: rng.range(0, Math.PI * 2),
        argumentOfPeriapsis: rng.range(0, Math.PI * 2),
        trueAnomaly: rng.range(0, Math.PI * 2),
    };
}
/** How far a satellite's plane may stray from the plane of what it orbits. */
const SATELLITE_PLANE_TOLERANCE = (10 * Math.PI) / 180;
/**
 * An orbit around a planet rather than around the star.
 *
 * Satellites keep close to their host's own orbital plane — within ten degrees
 * — rather than being tilted independently. Rolling them freely made moons and
 * stations appear to orbit at wild angles to the world they belong to, which
 * read as unrelated objects that happened to be nearby.
 */
function makeSatelliteOrbit(rng, host, semiMajorAxis, eccentricityMax = ORBIT_ECCENTRICITY_MAX) {
    const wobble = () => rng.range(-SATELLITE_PLANE_TOLERANCE, SATELLITE_PLANE_TOLERANCE);
    return {
        semiMajorAxis,
        eccentricity: rng.range(0, eccentricityMax),
        inclination: host.inclination + wobble(),
        ascendingNode: host.ascendingNode + wobble(),
        argumentOfPeriapsis: rng.range(0, Math.PI * 2),
        trueAnomaly: rng.range(0, Math.PI * 2),
    };
}
/**
 * Semi-major axis for the nth orbit.
 *
 * Each step is drawn from the *system's* stream rather than the planet's, so
 * every planet in a system agrees on where the orbits are. A planet cannot
 * compute its own axis in isolation — it depends on all the ones inside it.
 */
function orbitAxes(seed, systemId, count) {
    const rng = Rng.forEntity(seed, 'orbits', systemId);
    const axes = [];
    let axis = rng.range(ORBIT_BASE.min, ORBIT_BASE.max);
    for (let i = 0; i < count; i++) {
        axes.push(axis);
        axis *= rng.range(ORBIT_GROWTH.min, ORBIT_GROWTH.max);
    }
    return axes;
}
const MOON_CHANCE = 0.4;
/**
 * Planet type weights.
 *
 * Rocky and barren dominate because a galaxy where every other world is a
 * garden makes finding one meaningless. Gas giants are drawn from this table
 * too but rejected below `GAS_GIANT_MIN_RADIUS` — a small body cannot hold the
 * atmosphere — and fall back to rocky.
 */
const PLANET_TYPE_WEIGHTS = [
    ['rocky', 20],
    ['barren', 16],
    ['ice', 12],
    ['desert', 11],
    ['gasGiant', 10],
    ['ocean', 9],
    ['toxic', 8],
    ['jungle', 6],
    ['islandic', 5],
    ['volcanic', 3],
];
/**
 * Ice cap reach. Most worlds get none at all: caps everywhere made every planet
 * read as the same object with a different tint, and the poles were the first
 * thing the eye went to.
 */
function rollCapExtent(rng) {
    if (!rng.bool(0.45))
        return 0;
    return rng.range(0.08, 0.3);
}
function rollPlanetType(rng, radius) {
    const type = rng.weighted(PLANET_TYPE_WEIGHTS);
    return type === 'gasGiant' && radius < GAS_GIANT_MIN_RADIUS ? 'rocky' : type;
}
function generateMoons(seed, planet) {
    // Small worlds hold nothing. Of the large ones, only some do.
    if (planet.radius < MOON_MIN_PLANET_RADIUS)
        return [];
    const rng = Rng.forEntity(seed, 'moons', planet.id);
    if (!rng.bool(MOON_CHANCE))
        return [];
    const count = rng.weighted([[1, 55], [2, 30], [3, 15]]);
    return Array.from({ length: count }, (_, i) => {
        const id = `${planet.id}-m${i}`;
        const moonRng = Rng.forEntity(seed, 'moon', id);
        const palette = rollMoonPalette(moonRng);
        return {
            id,
            planetId: planet.id,
            name: `${planet.name}${String.fromCharCode(97 + i)}`,
            // Stepped out from the planet's surface so moons never sit inside it or
            // on top of each other.
            orbit: makeSatelliteOrbit(moonRng, planet.orbit, planet.radius * (2.4 + i * 1.3), 0.04),
            radius: planet.radius * moonRng.range(0.14, 0.28),
            // Ice is common out here, volcanic is the rare tidally-cooked one worth
            // finding. Nothing that implies an atmosphere.
            type: moonRng.weighted([
                ['rocky', 42],
                ['barren', 33],
                ['ice', 19],
                ['volcanic', 6],
            ]),
            shaderSeed: moonRng.int(0, 0xffff),
            baseColor: palette.base,
            accentColor: palette.accent,
            highlightColor: palette.highlight,
            // Airless bodies do not hold polar caps the way a planet does; the ice
            // ones are ice all over rather than ice at the ends.
            capExtent: 0,
        };
    });
}
function generatePlanet(seed, systemId, systemName, index, semiMajorAxis) {
    const id = `${systemId}-p${index}`;
    const rng = Rng.forEntity(seed, 'planet', id);
    const radius = rng.range(PLANET_RADIUS.min, PLANET_RADIUS.max);
    const palette = rollPlanetPalette(rng);
    const core = {
        id,
        systemId,
        name: makeDesignatedName(systemName, index),
        type: rollPlanetType(rng, radius),
        orbit: makeOrbit(rng, semiMajorAxis),
        radius,
        shaderSeed: rng.int(0, 0xffff),
        baseColor: palette.base,
        accentColor: palette.accent,
        highlightColor: palette.highlight,
        capExtent: rollCapExtent(rng),
        scansToComplete: rng.int(3, 6),
    };
    return { ...core, moons: generateMoons(seed, core) };
}
/**
 * Station allegiance is decided here and never anywhere else — it is fixed by
 * the seed, cannot be captured, and is therefore never stored.
 */
const ALLEGIANCE_WEIGHTS = [
    [null, 30],
    ['red', 15],
    ['blue', 15],
    ['green', 15],
    ['yellow', 15],
    // Rarer, matching its "least benefits" role — it should feel like a fallback,
    // not a default.
    ['turquoise', 10],
];
function generateStation(seed, systemId, index, forcedType, 
/** Planets not already hosting a station; claimed by removal. */
freePlanets, outerOrbit) {
    const id = `${systemId}-t${index}`;
    const rng = Rng.forEntity(seed, 'station', id);
    const nameRng = Rng.forEntity(seed, 'station-name', id);
    const type = forcedType ?? rng.weighted([
        ['outpost', 65],
        ['station', 35],
    ]);
    const isOutpost = type === 'outpost';
    const pads = generatePads(rng, isOutpost);
    // Anything this large is a structure in its own right, not something bolted
    // into a planet's gravity well, so it takes its own orbit around the star.
    const mustOrbitStar = stationSizeScore(pads) >= SUN_ORBIT_SCORE;
    // A planet supports one station and no more — two sharing an orbit read as a
    // duplicate rather than as a busy world. Claiming removes it from the pool.
    const wantsPlanet = freePlanets.length > 0 && !mustOrbitStar && rng.bool(0.75);
    const host = wantsPlanet ? freePlanets.splice(rng.int(0, freePlanets.length - 1), 1)[0] : null;
    const orbit = host
        ? makeSatelliteOrbit(rng, host.orbit, host.radius * rng.range(3.2, 6.5), 0.05)
        : makeOrbit(rng, rng.range(outerOrbit * 0.35, outerOrbit * 1.15), 0.08);
    return {
        id,
        systemId,
        name: makeStationName(nameRng),
        type,
        allegiance: rng.weighted(ALLEGIANCE_WEIGHTS),
        parent: host?.id ?? null,
        orbit,
        pads,
        // TODO(balance): facility odds are first-pass. Advanced fabricators are
        // deliberately scarce — they are the only way to resize a line.
        hasAdvancedFabricator: isOutpost ? false : rng.bool(0.45),
        hasPainter: isOutpost ? rng.bool(0.35) : rng.bool(0.75),
    };
}
/** No station anywhere may have fewer than this, or more than PAD_COUNT_MAX. */
export const PAD_COUNT_MIN = 2;
export const PAD_COUNT_MAX = 18;
const OUTPOST_PADS = { min: PAD_COUNT_MIN, max: 5 };
const STATION_PADS = { min: 6, max: PAD_COUNT_MAX };
/**
 * Pad counts per size, obeying the design rule that a station must have at
 * least as many of every smaller pad as it has of any larger one: one large pad
 * requires a medium and a small to go with it.
 *
 * Built monotonically from the top down so the rule holds by construction. The
 * two corrections afterwards preserve it as well — lowering the *largest*
 * non-empty tier cannot break `small >= medium >= large >= massive`, and nor
 * can raising the smallest.
 */
function rollPadCounts(rng, isOutpost) {
    const step = () => rng.weighted([[0, 42], [1, 34], [2, 17], [3, 7]]);
    // Outposts are small stations: nothing above medium ever berths there.
    const massive = isOutpost ? 0 : step();
    const large = isOutpost ? 0 : massive + step();
    const medium = large + step();
    const small = Math.max(1, medium + step());
    const counts = { small, medium, large, massive };
    const order = ['massive', 'large', 'medium', 'small'];
    const total = () => counts.small + counts.medium + counts.large + counts.massive;
    const max = isOutpost ? OUTPOST_PADS.max : STATION_PADS.max;
    const min = isOutpost ? OUTPOST_PADS.min : STATION_PADS.min;
    // Trim from the largest tier down; each removal keeps the ordering valid.
    for (const size of order) {
        while (total() > max && counts[size] > 0)
            counts[size]--;
    }
    while (total() < min)
        counts.small++;
    return counts;
}
function generatePads(rng, isOutpost) {
    const counts = rollPadCounts(rng, isOutpost);
    const pads = [];
    // Largest first, so pad-0 is always the most capable berth on the deck.
    for (const size of [...SIZE_CLASSES].reverse()) {
        for (let i = 0; i < counts[size]; i++) {
            pads.push({ id: `pad-${pads.length}`, size });
        }
    }
    return pads;
}
/** Full star-circling belts are the rare case; most debris rings a planet. */
const BELT_CHANCE = 0.18;
/**
 * Where a star-circling belt can go without crossing anything.
 *
 * A belt spans its entire orbit, so unlike a point body it cannot simply be
 * dropped at a random radius — it would slice through every planet whose orbit
 * it crossed. The gaps between consecutive orbits are the only legal homes, and
 * because orbits grow multiplicatively those gaps are generous.
 */
function findOrbitalGaps(planets, outerOrbit) {
    if (planets.length === 0)
        return [{ inner: outerOrbit * 0.4, outer: outerOrbit * 1.4 }];
    const gaps = [];
    const first = planets[0];
    // Inside the innermost orbit, if there is usable room.
    const innerEdge = periapsis(first.orbit) - first.radius;
    if (innerEdge > 400)
        gaps.push({ inner: 200, outer: innerEdge });
    for (let i = 1; i < planets.length; i++) {
        const inside = planets[i - 1];
        const outside = planets[i];
        gaps.push({
            inner: apoapsis(inside.orbit) + inside.radius,
            outer: periapsis(outside.orbit) - outside.radius,
        });
    }
    // And beyond the outermost, which is always available.
    const last = planets[planets.length - 1];
    gaps.push({
        inner: apoapsis(last.orbit) + last.radius,
        outer: apoapsis(last.orbit) * 1.9,
    });
    return gaps.filter((gap) => gap.outer - gap.inner > 200);
}
/**
 * All of a system's debris at once.
 *
 * Batched rather than generated per-field because two of the rules are about
 * the set as a whole: a planet may host at most one field, and two belts must
 * not be assigned the same orbital gap. Neither can be enforced by a function
 * that only sees one field.
 */
function generateAsteroidFields(seed, systemId, count, planets, outerOrbit) {
    const chooser = Rng.forEntity(seed, 'fields', systemId);
    // Consumed as they are claimed, so neither can be handed out twice.
    const freePlanets = chooser.shuffle(planets);
    const freeGaps = chooser.shuffle(findOrbitalGaps(planets, outerOrbit));
    // Beyond the outermost orbit there is always room, so a belt can go here when
    // every gap is taken. This is what guarantees the requested count is always
    // met — `getSystemSummary` promises a number without generating any of this,
    // and a system that quietly produced fewer would make the map lie.
    let frontier = outerOrbit * 2.1;
    const fields = [];
    for (let index = 0; index < count; index++) {
        const id = `${systemId}-f${index}`;
        const rng = Rng.forEntity(seed, 'field', id);
        const nameRng = Rng.forEntity(seed, 'field-name', id);
        // A belt if asked for, or if there is no planet left to ring.
        const isBelt = rng.bool(BELT_CHANCE) || freePlanets.length === 0;
        if (isBelt) {
            const gap = freeGaps.pop();
            const span = gap ? gap.outer - gap.inner : frontier * 0.4;
            // Centred in its gap, and never wide enough to reach either edge.
            const width = Math.min(rng.range(90, 320), span * 0.35);
            const axis = gap ? gap.inner + span * 0.5 : frontier;
            if (!gap)
                frontier *= 1.4;
            fields.push({
                id,
                systemId,
                name: `${makeName(nameRng)} Belt`,
                kind: 'belt',
                parent: null,
                // Nearly flat and nearly circular, or a belt stops reading as a ring
                // and just looks like scattered debris.
                orbit: {
                    ...makeOrbit(rng, axis, 0.02),
                    inclination: rollInclination(rng) * 0.25,
                },
                radius: width,
                richness: rng.range(0.5, 2),
                regenHours: 24,
            });
            continue;
        }
        const host = freePlanets.pop();
        fields.push({
            id,
            systemId,
            name: `${makeName(nameRng)} Field`,
            kind: 'cluster',
            parent: host.id,
            // A ring around its planet, clear of the surface and of any moon.
            orbit: {
                ...makeSatelliteOrbit(rng, host.orbit, host.radius * rng.range(1.6, 2.2), 0.02),
            },
            radius: host.radius * rng.range(0.25, 0.5),
            richness: rng.range(0.5, 2),
            // Matches the 24h depletion TTL on world_deltas — a field is fully back
            // by the time its delta row stops meaning anything.
            regenHours: 24,
        });
    }
    return fields;
}
const ANOMALY_KINDS = ['signal', 'derelict', 'rift', 'nebula'];
function generateAnomaly(seed, systemId, index, outerOrbit) {
    const id = `${systemId}-a${index}`;
    const rng = Rng.forEntity(seed, 'anomaly', id);
    const nameRng = Rng.forEntity(seed, 'anomaly-name', id);
    return {
        id,
        systemId,
        name: `${makeName(nameRng)} Anomaly`,
        kind: rng.pick(ANOMALY_KINDS),
        // Always the star: an anomaly tied to a planet would read as that planet's
        // feature rather than as something strange found in open space. Wilder
        // inclinations than anything else, since they are meant to look wrong.
        parent: null,
        orbit: makeOrbit(rng, rng.range(outerOrbit * 0.5, outerOrbit * 1.6), 0.25),
        scansToComplete: rng.int(2, 5),
    };
}
// ─── Docking ─────────────────────────────────────────────────────────────────
/**
 * Size class of a ship, from the extent of its structure.
 *
 * Deliberately based on the bounding box rather than enclosed area: what stops
 * a ship fitting a pad is how wide it is, not how much of it is hull.
 */
export function shipSizeClass(joints) {
    if (joints.length === 0)
        return 'small';
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const joint of joints) {
        if (joint.x < minX)
            minX = joint.x;
        if (joint.x > maxX)
            maxX = joint.x;
        if (joint.y < minY)
            minY = joint.y;
        if (joint.y > maxY)
            maxY = joint.y;
    }
    const extent = Math.max(maxX - minX, maxY - minY);
    // Thresholds sit under each pad's side length (25/50/70/100) with clearance,
    // so a ship classed for a pad genuinely fits inside it with room to
    // manoeuvre. Anything wider than the largest berth is still classed massive —
    // the docking check refuses it rather than this silently mislabelling it.
    if (extent <= 20)
        return 'small';
    if (extent <= 42)
        return 'medium';
    if (extent <= 60)
        return 'large';
    return 'massive';
}
//# sourceMappingURL=generate.js.map